import { Component, inject } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { signUp } from '@aws-amplify/auth';
import { CommonModule } from '@angular/common';
import { LocaleService } from 'src/app/services/locale';
import { InputComponent } from '../shared/input';
import { DropdownComponent } from '../shared/dropdown';
import { ButtonComponent } from '../shared/button';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'app-register',
  imports: [
    ReactiveFormsModule,
    RouterModule,
    CommonModule,
    InputComponent,
    DropdownComponent,
    ButtonComponent
  ],
  template: `
    <div class="flex flex-col gap-3 sm:gap-4 mb-4 sm:mb-6">
      <h1 class="text-xl sm:text-2xl lg:text-3xl font-bold futura text-[#043B22]">
        {{ locale.register.heading }}
      </h1>
      <p class="text-xs sm:text-sm text-[#616161]">
        {{ locale.register.sub_already_have_account }}
        <a routerLink="/login" class="text-xs sm:text-sm text-[#043B22] font-semibold hover:text-[#043B22]/80 transition-colors underline underline-offset-2">{{
          locale.register.sub_already_have_account_s
        }}</a>
      </p>
    </div>
    <form class="flex flex-col gap-3 sm:gap-4" [formGroup]="form" (submit)="submit()">
      <app-input
        [keyUp]="reset"
        type="email"
        label="{{ locale.common.lbl_email }}"
        placeholder="{{ locale.common.placeholder_email }}"
        [control]="form.controls['email']"
      ></app-input>
      <app-dropdown
        [options]="[
          {
            label: locale.register.usertype_option_company,
            value: 'company'
          },
          {
            label: locale.register.usertype_option_consultant,
            value: 'consultant'
          }
        ]"
        label="{{ locale.register.lbl_usertype }}"
        placeholder="{{ locale.register.placeholder_usertype }}"
        [control]="form.controls['userType']"
      ></app-dropdown>
      <app-input
        [keyUp]="reset"
        type="password"
        label="{{ locale.common.lbl_password }}"
        placeholder="{{ locale.common.placeholder_password }}"
        [control]="form.controls['password']"
      ></app-input>
      <app-input
        [keyUp]="reset"
        type="password"
        label="{{ locale.register.lbl_repeat_password }}"
        placeholder="{{ locale.common.placeholder_password }}"
        [control]="form.controls['repeatPassword']"
      ></app-input>
      <div *ngIf="error.length" class="text-red-500 text-xs sm:text-sm bg-red-50 p-3 rounded-lg">
        {{ error }}
      </div>
      <div class="mt-1 sm:mt-2">
        <app-button
          [disabled]="form.invalid"
          type="submit"
          text="{{ locale.register.button_create }}"
        ></app-button>
      </div>
    </form>
  `
})
export class RegisterComponent {
  private router: Router = inject(Router);
  private spinner = inject(NgxSpinnerService);
  locale = inject(LocaleService).locale();

  error = '';

  form = new FormGroup(
    {
      email: new FormControl('', [Validators.required, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)]),
      password: new FormControl('', Validators.required),
      repeatPassword: new FormControl('', Validators.required),
      userType: new FormControl('', Validators.required)
    },
    {
      validators: this.matchPassword
    }
  );

  private matchPassword(control: AbstractControl) {
    const password = control.get('password')?.value;
    const repeatPassword = control.get('repeatPassword')?.value;

    if (password !== repeatPassword) {
      control.get('repeatPassword')?.setErrors({ passwordMismatch: true });
    } else {
      control.get('repeatPassword')?.setErrors(null);
    }

    return null;
  }

  submit = async () => {
    try {
      this.spinner.show();

      const credentials = {
        username: this.form.controls.email.value ?? '',
        password: this.form.controls.password.value ?? ''
      };

      const user = await signUp(credentials);

      if (user.nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
        this.router.navigate(['/verify'], {
          state: {
            username: credentials.username,
            password: credentials.password
          }
        });
      }

      this.spinner.hide();
    } catch (error) {
      this.spinner.hide();
      this.error = (error as any).message;
    }
  };

  reset = () => {
    this.error = '';
  };
}
